<template>
  <DSDateTimeFormField
    ref="fieldRef"
    v-model:input="inputModelValue"
    v-model:open="popoverOpen"
    :name="props.name"
    :label="props.label"
    :inline-edit="props.inlineEdit"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :disabled="props.disabled"
    :placeholder="placeholder"
    action-icon="time-clock"
    :error-messages="errorMessages"
    :clearable="props.clearable"
    :rules="rules"
    @blur="handleBlur"
    @focus="emit('focus', $event)"
    @close="handleClose"
    @enter="commitTypedTime"
    @escape="discardTypedTime"
  >
    <DSTimePickerClock
      v-model="pickerModelValue"
      :disabled="props.disabled"
      @close:popover="popoverOpen = false"
    />
  </DSDateTimeFormField>
  <input
    type="hidden"
    :name="props.name"
    :value="hiddenInputValue"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { DSTimePickerEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSTimePickerClock from "@/src/design_system/form_elements/timepicker/components/DSTimePickerClock.vue";
import { easySettings } from "@/src/shared/store/easyGlobalSettings";
import type { DSTimePickerProps } from "@/src/design_system/form_elements/timepicker/types/timepicker";
import {
  DS_DATE_TIME_PICKER_MODE,
  DSDateTimePickerModelValue,
} from "@/src/design_system/form_elements/shared/types/dateTimePicker";
import DSDateTimeFormField from "@/src/design_system/form_elements/shared_modules/date_time_form_field/components/DSDateTimeFormField.vue";
import type { DSDateTimeFormFieldInputModelValue } from "@/src/design_system/form_elements/shared_modules/date_time_form_field/types";
import { useDateTimePickers } from "@/src/design_system/form_elements/shared/composables/useDateTimePickers";
import { DEFAULT_DATE_TIME_PLACEHOLDER } from "@/src/design_system/form_elements/shared/constants/dateTimePlaceholders";
import { BE_TIME_FORMAT } from "@/src/shared/constants/date";
import { parseTimeOfDay } from "@/src/shared/utils/timeOfDay";
import dayjs from "dayjs";

const props = withDefaults(defineProps<DSTimePickerProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  tooltip: undefined,
  clearable: true,
  rules: undefined,
});

const modelValue = defineModel<DSDateTimePickerModelValue>({ default: null });
const emit = defineEmits<DSTimePickerEmits>();
const fieldRef = ref<InstanceType<typeof DSDateTimeFormField>>();
const { focus, blur } = useDSInput(fieldRef);

const defaultPlaceholder = computed(() =>
  dayjs(DEFAULT_DATE_TIME_PLACEHOLDER).format(easySettings.getGlobalTimeFormat.value)
);

const placeholder = computed(() => props.placeholder ?? defaultPlaceholder.value);

const { formattedModelValue, pickerModelValue, temporalFormattedModelValue, popoverOpen, hiddenInputValue } =
  useDateTimePickers(DS_DATE_TIME_PICKER_MODE.Time, modelValue, {
    input: easySettings.getGlobalTimeFormat.value,
    hiddenInput: BE_TIME_FORMAT,
  });

const hasInvalidTime = ref(false);
const showsInvalidTime = computed(() => hasInvalidTime.value && temporalFormattedModelValue.value !== null);
const invalidTimeMessage = computed(() =>
  window.I18n.designSystem.timePicker.invalidTime.replace("%{example}", () => defaultPlaceholder.value)
);

const errorMessages = computed(() =>
  showsInvalidTime.value ? [...(props.errorMessages ?? []), invalidTimeMessage.value] : props.errorMessages
);

const rules = computed(() => [
  ...(props.rules ?? []),
  () => (showsInvalidTime.value ? invalidTimeMessage.value : true),
]);

const inputModelValue = computed<DSDateTimeFormFieldInputModelValue>({
  get: () => formattedModelValue.value,
  set: (value: DSDateTimeFormFieldInputModelValue) => {
    hasInvalidTime.value = false;
    formattedModelValue.value = value;
  },
});

// Plain Rails forms have no DS form store, so the browser's constraint validation blocks their submit.
watch(showsInvalidTime, invalid => {
  fieldRef.value?.getInputElement()?.setCustomValidity(invalid ? invalidTimeMessage.value : "");
});

watch(
  () => modelValue.value?.format(BE_TIME_FORMAT),
  () => {
    temporalFormattedModelValue.value = null;
  }
);

// Keeps an enclosing dialog open when Escape only cancels the typed text.
const discardTypedTime = (event?: KeyboardEvent) => {
  if (temporalFormattedModelValue.value === null) return;
  event?.stopPropagation();
  temporalFormattedModelValue.value = null;
};

const commitTypedTime = () => {
  const typedText = temporalFormattedModelValue.value;
  if (typedText === null) return;

  const time = parseTimeOfDay(typedText);
  if (!time) {
    hasInvalidTime.value = true;
    return;
  }

  modelValue.value = dayjs(time, BE_TIME_FORMAT, true);
  temporalFormattedModelValue.value = null;
};

const handleBlur = (event: FocusEvent) => {
  commitTypedTime();
  emit("blur", event);
};

const handleClose = () => {
  commitTypedTime();
  emit("close");
};

defineExpose({ focus, blur });
</script>
